import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import {
  Sparkles, CalendarDays, CalendarRange, Calendar, CalendarClock,
  Plus, Trash2, Check, Sprout, Bell, X, Edit3, QrCode, Image as ImageIcon, Upload, Printer,
  HelpCircle,
} from 'lucide-react';
import { toast } from 'sonner';
import { QRCodeSVG } from 'qrcode.react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const FREQ_META = {
  daily:   { label: 'Dag', icon: CalendarDays, color: 'bg-[#B9FBC0]' },
  weekly:  { label: 'Vecka', icon: CalendarDays, color: 'bg-[#A2D2FF]' },
  monthly: { label: 'Månad', icon: Calendar, color: 'bg-[#FFC8DD]' },
  q3: { label: '3 mån', icon: CalendarRange, color: 'bg-[#CDB4DB]' },
  q6: { label: '6 mån', icon: CalendarRange, color: 'bg-[#FDF0D5]' },
  yearly: { label: 'År', icon: CalendarClock, color: 'bg-[#B9FBC0]' },
};

const DAYS_SV = ['Må', 'Ti', 'On', 'To', 'Fr', 'Lö', 'Sö'];

function CleaningHelpBanner() {
  const [open, setOpen] = useState(() => {
    try { return localStorage.getItem('cleaning_help_dismissed') !== '1'; } catch { return true; }
  });
  const close = () => {
    setOpen(false);
    try { localStorage.setItem('cleaning_help_dismissed', '1'); } catch { /* ignore */ }
  };
  const reopen = () => setOpen(true);
  if (!open) {
    return (
      <button
        onClick={reopen}
        className="neo-btn px-3 py-1.5 bg-[#FFF0F5] text-xs font-bold flex items-center gap-1"
        data-testid="cleaning-help-reopen"
      >
        <HelpCircle className="w-3.5 h-3.5" /> Hur funkar Städ?
      </button>
    );
  }
  return (
    <div className="neo-card p-4 bg-gradient-to-br from-[#FFF0F5] via-[#E8F5FF] to-[#F3E8FF]" data-testid="cleaning-help-banner">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-bold flex items-center gap-2">
          <HelpCircle className="w-4 h-4" /> Hur funkar Städ?
        </h3>
        <button
          onClick={close}
          className="neo-btn p-1 bg-white"
          aria-label="Dölj guiden"
          data-testid="cleaning-help-close"
        >
          <X className="w-3.5 h-3.5" />
        </button>
      </div>
      <ul className="text-xs space-y-1.5 text-black/80 leading-snug">
        <li>🟣 <b>QR</b> (lila knapp vid uppgift) → skriv ut en enkel QR-kod att sätta vid t.ex. skåpet. Skanna = klart!</li>
        <li>✏️ <b>Ändra</b> → där lägger du till en <b>referensbild</b> (så här ska det se ut). Bilden visas som liten thumbnail bredvid uppgiften.</li>
        <li>🖨️ <b>"Skriv ut alla QR"</b> → längst ner i listan för alla uppgifter. Ett A4 med alla QR-koder.</li>
        <li>☀️ / 🌙 <b>Morgon/Kväll-rutin</b> → guidad kedja. Klarar en sak → nästa dyker upp.</li>
        <li>🔔 <b>Påminn mig</b> → notis varje morgon med dagens uppgifter.</li>
      </ul>
      <p className="text-[10px] text-black/50 mt-2 italic">Behöver du hitta fler funktioner? Klicka på <b>Hjälp</b> uppe i headern eller tryck <kbd className="border border-black/30 rounded px-1">Skift</kbd>+<kbd className="border border-black/30 rounded px-1">?</kbd>.</p>
    </div>
  );
}

function DayPicker({ value, onChange }) {
  const selected = value || [];
  const toggle = (i) => {
    const next = selected.includes(i) ? selected.filter((d) => d !== i) : [...selected, i].sort();
    onChange(next.length === 0 ? null : next);
  };
  return (
    <div className="flex gap-1 flex-wrap">
      {DAYS_SV.map((d, i) => (
        <button
          key={i}
          onClick={() => toggle(i)}
          type="button"
          className={`w-9 h-9 rounded-full border-2 border-black text-xs font-bold ${selected.includes(i) ? 'bg-[#A2D2FF]' : 'bg-white'}`}
          data-testid={`cleaning-day-${i}`}
        >
          {d}
        </button>
      ))}
      {selected.length > 0 && (
        <button
          onClick={() => onChange(null)}
          type="button"
          className="px-2 h-9 rounded-lg border-2 border-black text-[10px] bg-white"
          title="Visa varje dag (ta bort veckodagsval)"
        >
          Rensa
        </button>
      )}
    </div>
  );
}

function TaskRow({ task, onComplete, onUncomplete, onDelete, onEdit, onShowQR, onShowImage }) {
  const isDone = task.done_this_period;
  const meta = FREQ_META[task.frequency] || FREQ_META.weekly;
  return (
    <div className={`rounded-xl border-2 border-black p-2.5 transition-all ${isDone ? 'bg-[#B9FBC0]/60' : 'bg-white dark:bg-[#2d2d54]'}`} data-testid={`cleaning-task-${task.id}`}>
      <div className="flex items-center gap-2">
        <button
          onClick={() => (isDone ? onUncomplete(task) : onComplete(task))}
          className={`flex flex-col items-center justify-center flex-shrink-0 rounded-xl border-2 border-black transition-all min-w-[56px] px-1.5 py-1.5 ${isDone ? 'bg-[#B9FBC0] shadow-inner' : 'bg-white hover:bg-[#E8F5E8] shadow-[2px_2px_0_#000]'}`}
          data-testid={`cleaning-check-${task.id}`}
          title={isDone ? 'Ångra' : 'Markera som klar'}
        >
          <div className={`w-5 h-5 rounded-full border-2 border-black flex items-center justify-center ${isDone ? 'bg-white' : 'bg-[#FDF0D5]'}`}>
            {isDone && <Check className="w-3.5 h-3.5 stroke-[3]" />}
          </div>
          <span className="text-[9px] font-bold mt-0.5 leading-none">{isDone ? 'Klar!' : 'Markera'}</span>
        </button>
        {task.reference_image && (
          <button
            onClick={() => onShowImage(task)}
            className="w-9 h-9 rounded-lg border-2 border-black flex-shrink-0 overflow-hidden hover:scale-110 transition-transform"
            title="Så här ska det se ut"
            data-testid={`cleaning-ref-img-${task.id}`}
          >
            <img src={task.reference_image} alt="" className="w-full h-full object-cover" />
          </button>
        )}
        <div className="flex-1 min-w-0">
          <p className={`font-bold text-sm leading-tight ${isDone ? 'line-through opacity-70' : ''}`}>{task.title}</p>
          <div className="flex items-center gap-1 flex-wrap mt-0.5">
            <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full border-2 border-black ${meta.color}`}>
              {meta.label}
            </span>
            <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border-2 border-black bg-[#FDF0D5]">
              +{task.points}p
            </span>
            {task.routine === 'morning' && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border-2 border-black bg-[#FFE4B5]">☀️ Morgon</span>}
            {task.routine === 'evening' && <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-full border-2 border-black bg-[#C8B6FF]">🌙 Kväll</span>}
            {task.frequency === 'weekly' && task.days_of_week && task.days_of_week.length > 0 && (
              <span className="text-[9px] opacity-70">
                {task.days_of_week.map((d) => DAYS_SV[d]).join('/')}
              </span>
            )}
          </div>
        </div>
        {/* Actions — responsive stacked icons with text labels */}
        <div className="flex items-center gap-1 flex-shrink-0">
          <button onClick={() => onShowQR(task)} className="neo-btn px-1.5 py-1 bg-[#E8D5FF] flex flex-col items-center gap-0" title="QR-kod (skriv ut)" data-testid={`cleaning-qr-${task.id}`} aria-label="QR-kod">
            <QrCode className="w-3.5 h-3.5" />
            <span className="text-[8px] font-bold leading-none mt-0.5">QR</span>
          </button>
          <button onClick={() => onEdit(task)} className="neo-btn px-1.5 py-1 bg-white flex flex-col items-center gap-0" title="Redigera (lägg till bild)" data-testid={`cleaning-edit-${task.id}`} aria-label="Redigera">
            <Edit3 className="w-3.5 h-3.5" />
            <span className="text-[8px] font-bold leading-none mt-0.5">Ändra</span>
          </button>
          <button onClick={() => onDelete(task)} className="neo-btn px-1.5 py-1 bg-white hover:bg-red-100 flex flex-col items-center gap-0" title="Ta bort" data-testid={`cleaning-delete-${task.id}`} aria-label="Ta bort">
            <Trash2 className="w-3.5 h-3.5" />
            <span className="text-[8px] font-bold leading-none mt-0.5">Bort</span>
          </button>
        </div>
      </div>
    </div>
  );
}

function ProgressOverview({ today, tasks }) {
  const dailyTotal = tasks.filter((t) => t.frequency === 'daily').length;
  const dailyDone = tasks.filter((t) => t.frequency === 'daily' && t.done_this_period).length;
  const todayTotal = (today?.items || []).length; // items due today across all frequencies
  const todayDone = (today?.items || []).filter((t) => t.done_this_period).length;
  const weeklyTotal = tasks.filter((t) => t.frequency === 'weekly').length;
  const weeklyDone = tasks.filter((t) => t.frequency === 'weekly' && t.done_this_period).length;
  const monthlyTotal = tasks.filter((t) => t.frequency === 'monthly').length;
  const monthlyDone = tasks.filter((t) => t.frequency === 'monthly' && t.done_this_period).length;
  const dailyPct = dailyTotal ? Math.round((dailyDone / dailyTotal) * 100) : 0;
  const todayPct = todayTotal ? Math.round((todayDone / todayTotal) * 100) : 0;
  const weeklyPct = weeklyTotal ? Math.round((weeklyDone / weeklyTotal) * 100) : 0;
  const monthlyPct = monthlyTotal ? Math.round((monthlyDone / monthlyTotal) * 100) : 0;
  const allDailyDone = dailyTotal > 0 && dailyDone === dailyTotal;
  return (
    <div className="neo-card p-4 bg-gradient-to-br from-[#FFF3F9] via-[#F3E8FF] to-[#E8F5FF]" data-testid="cleaning-progress-overview">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h3 className="font-['Fredoka'] font-bold text-lg flex items-center gap-2">
          📊 Min städöversikt
        </h3>
        {allDailyDone && <span className="text-xs font-bold bg-[#B9FBC0] border-2 border-black rounded-full px-3 py-1">🎉 Alla dagliga klara!</span>}
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Dagligt', done: dailyDone, total: dailyTotal, pct: dailyPct, color: '#B9FBC0' },
          { label: 'Idag', done: todayDone, total: todayTotal, pct: todayPct, color: '#FFC8DD' },
          { label: 'Veckans', done: weeklyDone, total: weeklyTotal, pct: weeklyPct, color: '#A2D2FF' },
          { label: 'Månadens', done: monthlyDone, total: monthlyTotal, pct: monthlyPct, color: '#CDB4DB' },
        ].map((b) => (
          <div key={b.label} className="neo-card bg-white p-3">
            <div className="flex items-baseline justify-between mb-1">
              <p className="text-xs font-bold opacity-70">{b.label}</p>
              <p className="font-['Fredoka'] font-bold text-lg">
                {b.done}<span className="opacity-50 text-sm">/{b.total || '—'}</span>
              </p>
            </div>
            <div className="h-2.5 bg-black/10 rounded-full overflow-hidden border border-black">
              <div className="h-full transition-all" style={{ width: `${b.pct}%`, backgroundColor: b.color }} />
            </div>
            <p className="text-[10px] opacity-60 mt-1">
              {b.total === 0 ? 'Inget här' : `${b.pct}% · ${Math.max(0, b.total - b.done)} kvar`}
            </p>
          </div>
        ))}
      </div>
      <p className="text-[11px] opacity-60 italic mt-2">💡 Dagliga uppgifter återställs varje natt. Veckouppgifter ligger kvar tills nästa måndag.</p>
    </div>
  );
}

function TaskEditor({ existing, onSave, onCancel }) {
  const [title, setTitle] = useState(existing?.title || '');
  const [frequency, setFrequency] = useState(existing?.frequency || 'weekly');
  const [points, setPoints] = useState(existing?.points ?? 10);
  const [daysOfWeek, setDaysOfWeek] = useState(existing?.days_of_week || null);
  const [referenceImage, setReferenceImage] = useState(existing?.reference_image || '');
  const [routine, setRoutine] = useState(existing?.routine || '');
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);

  const onPickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Bilden är för stor (max 5MB)');
      return;
    }
    // Compress via canvas to keep base64 < 200KB
    const img = new Image();
    const reader = new FileReader();
    reader.onload = () => {
      img.onload = () => {
        const max = 640;
        const ratio = Math.min(max / img.width, max / img.height, 1);
        const canvas = document.createElement('canvas');
        canvas.width = Math.round(img.width * ratio);
        canvas.height = Math.round(img.height * ratio);
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        setReferenceImage(canvas.toDataURL('image/jpeg', 0.75));
      };
      img.src = reader.result;
    };
    reader.readAsDataURL(file);
  };

  const save = async () => {
    if (!title.trim()) { toast.error('Skriv en titel'); return; }
    setSaving(true);
    try {
      await onSave({
        title: title.trim(),
        frequency,
        points: Number(points) || 10,
        days_of_week: frequency === 'weekly' ? daysOfWeek : null,
        reference_image: referenceImage || '',
        routine: routine || '',
      });
    } finally { setSaving(false); }
  };

  return (
    <div className="neo-card p-4 bg-white dark:bg-[#252545] space-y-3" data-testid="cleaning-editor">
      <div className="flex items-center justify-between">
        <h4 className="font-bold font-['Fredoka']">{existing ? 'Redigera' : 'Lägg till uppgift'}</h4>
        <button onClick={onCancel} className="neo-btn p-1 bg-white"><X className="w-4 h-4" /></button>
      </div>
      <input
        type="text"
        placeholder="T.ex. Dammsug vardagsrum"
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        maxLength={120}
        className="w-full px-3 py-2 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54]"
        data-testid="cleaning-title-input"
      />
      <div>
        <p className="text-xs font-bold mb-1">Frekvens</p>
        <div className="flex flex-wrap gap-1">
          {Object.entries(FREQ_META).map(([k, m]) => (
            <button
              key={k}
              onClick={() => setFrequency(k)}
              type="button"
              className={`neo-btn px-2 py-1 text-xs ${frequency === k ? m.color + ' ring-2 ring-black' : 'bg-white'}`}
              data-testid={`cleaning-freq-${k}`}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>
      {frequency === 'weekly' && (
        <div>
          <p className="text-xs font-bold mb-1">Dagar i veckan (valfritt — inget val = varje dag)</p>
          <DayPicker value={daysOfWeek} onChange={setDaysOfWeek} />
        </div>
      )}
      <label className="block">
        <span className="text-xs font-bold">Poäng</span>
        <input
          type="number"
          min={1}
          max={200}
          value={points}
          onChange={(e) => setPoints(e.target.value)}
          className="mt-1 w-24 px-3 py-2 rounded-xl border-2 border-black text-sm bg-white dark:bg-[#2d2d54]"
          data-testid="cleaning-points-input"
        />
      </label>
      <div>
        <p className="text-xs font-bold mb-1 flex items-center gap-1"><ImageIcon className="w-3 h-3" /> Referensbild (så här ska det se ut)</p>
        {referenceImage ? (
          <div className="flex gap-2 items-start">
            <img src={referenceImage} alt="" className="w-24 h-24 object-cover rounded-xl border-2 border-black" data-testid="cleaning-ref-preview" />
            <button onClick={() => setReferenceImage('')} className="neo-btn px-2 py-1 text-[10px] bg-white" data-testid="cleaning-ref-remove">Ta bort bild</button>
          </div>
        ) : (
          <>
            <input ref={fileRef} type="file" accept="image/*" onChange={onPickImage} className="hidden" data-testid="cleaning-ref-input" />
            <button onClick={() => fileRef.current?.click()} type="button" className="neo-btn px-3 py-2 text-xs bg-[#E8D5FF] flex items-center gap-1" data-testid="cleaning-ref-upload">
              <Upload className="w-3 h-3" /> Ladda upp bild
            </button>
          </>
        )}
      </div>
      <div>
        <p className="text-xs font-bold mb-1">🐾 Rutin (valfritt)</p>
        <div className="flex flex-wrap gap-1">
          {[
            { k: '', label: 'Ingen', bg: 'bg-white' },
            { k: 'morning', label: '☀️ Morgon', bg: 'bg-[#FFE4B5]' },
            { k: 'evening', label: '🌙 Kväll', bg: 'bg-[#C8B6FF]' },
          ].map((r) => (
            <button
              key={r.k || 'none'}
              type="button"
              onClick={() => setRoutine(r.k)}
              className={`neo-btn px-2 py-1 text-xs font-bold ${routine === r.k ? r.bg + ' ring-2 ring-black' : 'bg-white'}`}
              data-testid={`cleaning-routine-${r.k || 'none'}`}
            >
              {r.label}
            </button>
          ))}
        </div>
      </div>
      <button
        onClick={save}
        disabled={saving}
        className="neo-btn w-full py-2 bg-[#B9FBC0] text-sm font-bold disabled:opacity-50"
        data-testid="cleaning-save"
      >
        {saving ? 'Sparar…' : existing ? 'Spara' : 'Lägg till'}
      </button>
    </div>
  );
}

function QRModal({ task, onClose }) {
  const [info, setInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/cleaning/tasks/${task.id}/scan-token`, { withCredentials: true });
        setInfo(data);
      } catch { toast.error('Kunde inte generera QR'); }
      finally { setLoading(false); }
    })();
  }, [task.id]);
  const printIt = () => {
    const w = window.open('', '_blank', 'width=600,height=800');
    if (!w || !info) return;
    const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({
      '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
    }[c]));
    const safeTitle = escapeHtml(task.title);
    const safeFreq = escapeHtml(FREQ_META[task.frequency]?.label || '');
    const safePoints = Number(task.points) || 0;
    const qrHtml = document.getElementById(`qr-${task.id}`)?.outerHTML || '';
    w.document.write(`
      <html><head><title>QR: ${safeTitle}</title>
      <style>
        body { font-family: sans-serif; padding: 40px; text-align: center; }
        h1 { font-size: 24px; margin: 0 0 8px; }
        p { color: #666; margin: 4px 0; }
        .qr { padding: 20px; border: 3px solid #000; border-radius: 16px; display: inline-block; background: white; margin: 20px 0; box-shadow: 4px 4px 0 #000; }
        .hint { font-size: 13px; margin-top: 20px; color: #888; }
      </style></head><body>
        <h1>🧽 ${safeTitle}</h1>
        <p>Skanna när du gjort uppgiften!</p>
        <div class="qr">${qrHtml}</div>
        <p class="hint">${safeFreq} · +${safePoints} poäng</p>
        <p class="hint">Klistra där uppgiften ska göras — köket, badrummet, tvättkorgen…</p>
      </body></html>
    `);
    w.document.close();
    setTimeout(() => w.print(), 300);
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-center justify-center p-4" onClick={onClose}>
      <div className="neo-card bg-white w-full max-w-sm p-5 text-center" onClick={(e) => e.stopPropagation()} data-testid="cleaning-qr-modal">
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-['Fredoka'] font-bold text-lg">QR-kod</h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-sm font-bold mb-1">{task.title}</p>
        <p className="text-xs opacity-60 mb-3">Skriv ut och klistra på platsen — skanna när det är gjort!</p>
        {loading ? (
          <p className="animate-pulse-soft py-8">Genererar…</p>
        ) : info ? (
          <>
            <div className="inline-block p-4 bg-white border-2 border-black rounded-xl">
              <QRCodeSVG id={`qr-${task.id}`} value={info.scan_url} size={200} level="M" includeMargin={false} />
            </div>
            <p className="text-[10px] opacity-60 mt-2 break-all">{info.scan_url}</p>
            <button onClick={printIt} className="neo-btn w-full mt-3 py-2 bg-[#E8D5FF] font-bold text-sm flex items-center justify-center gap-1" data-testid="cleaning-qr-print">
              <Printer className="w-4 h-4" /> Skriv ut
            </button>
          </>
        ) : <p className="text-sm opacity-60">Kunde inte ladda</p>}
      </div>
    </div>
  );
}

function ReferenceImageModal({ task, onClose }) {
  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose}>
      <div className="neo-card bg-white w-full max-w-md p-4" onClick={(e) => e.stopPropagation()} data-testid="cleaning-ref-modal">
        <div className="flex items-center justify-between mb-2">
          <h3 className="font-['Fredoka'] font-bold text-sm">✨ {task.title}</h3>
          <button onClick={onClose} className="neo-btn p-1.5 bg-white"><X className="w-4 h-4" /></button>
        </div>
        <p className="text-xs opacity-60 mb-2 italic">Så här ska det se ut när det är klart 💛</p>
        <img src={task.reference_image} alt="" className="w-full rounded-xl border-2 border-black" />
      </div>
    </div>
  );
}

function SummaryCard({ freq }) {
  const [data, setData] = useState(null);
  const fetch = useCallback(async () => {
    try {
      const periodMap = { weekly: 'week', monthly: 'month', q3: 'q3', q6: 'q6', yearly: 'year' };
      const { data } = await axios.get(`${API}/cleaning/summary?period=${periodMap[freq]}`, { withCredentials: true });
      setData(data);
    } catch { /* noop */ }
  }, [freq]);
  useEffect(() => { fetch(); }, [fetch]);
  if (!data) return null;
  const pct = data.total_tasks > 0 ? Math.round((data.done_count / data.total_tasks) * 100) : 0;
  return (
    <div className="neo-card p-4 bg-[#FFFAF0] dark:bg-[#1f1f3a]" data-testid={`cleaning-summary-${freq}`}>
      <div className="flex items-center justify-between flex-wrap gap-2">
        <h4 className="font-bold font-['Fredoka']">Sammanfattning · {data.label}</h4>
        <span className="text-xs font-bold px-3 py-1 rounded-full bg-[#FDF0D5] border-2 border-black">
          {data.done_count}/{data.total_tasks} · {pct}% · +{data.total_points_earned} p
        </span>
      </div>
      <div className="progress-bar mt-2" style={{ height: '0.5rem' }}>
        <div className="progress-fill bg-[#B9FBC0]" style={{ width: `${pct}%` }} />
      </div>
      {data.pending?.length > 0 && (
        <div className="mt-3">
          <p className="text-xs font-bold opacity-80 mb-1">Kvar att göra:</p>
          <ul className="text-xs space-y-0.5 opacity-80">
            {data.pending.slice(0, 8).map((p) => (
              <li key={p.id}>• {p.title} <span className="opacity-60">(+{p.points} p)</span></li>
            ))}
            {data.pending.length > 8 && <li className="italic opacity-60">+{data.pending.length - 8} till…</li>}
          </ul>
        </div>
      )}
    </div>
  );
}

function AchievementsCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/cleaning/achievements`, { withCredentials: true });
        setData(data);
      } catch { /* noop */ }
    })();
  }, []);
  if (!data) return null;
  return (
    <div className="neo-card p-4 bg-[#FDF0D5] dark:bg-[#1f1f3a]" data-testid="cleaning-achievements">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h4 className="font-bold font-['Fredoka'] flex items-center gap-1">
          🏆 Städ-medaljer
        </h4>
        <span className="text-xs font-bold px-3 py-1 rounded-full bg-white border-2 border-black">
          {data.unlocked_count}/{data.total}
        </span>
      </div>
      <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2">
        {data.items.map((a) => (
          <div
            key={a.id}
            className={`rounded-xl border-2 border-black p-2 text-center transition-all ${a.unlocked ? 'bg-[#B9FBC0]' : 'bg-white dark:bg-[#2d2d54]'}`}
            title={a.desc}
            data-testid={`achievement-${a.id}`}
          >
            <div className={`text-2xl mb-1 ${a.unlocked ? '' : 'grayscale opacity-40'}`}>{a.emoji}</div>
            <p className={`text-[10px] font-bold leading-tight ${a.unlocked ? '' : 'opacity-70'}`}>{a.title}</p>
            <div className="progress-bar mt-1" style={{ height: '0.3rem' }}>
              <div
                className={`progress-fill ${a.unlocked ? 'bg-[#F0C040]' : 'bg-[#A2D2FF]'}`}
                style={{ width: `${a.percent}%` }}
              />
            </div>
            <p className="text-[9px] mt-0.5 opacity-70">{a.progress}/{a.target}</p>
          </div>
        ))}
      </div>
    </div>
  );
}

function InsightsCard() {
  const [data, setData] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/cleaning/insights`, { withCredentials: true });
        setData(data);
      } catch { /* noop */ }
    })();
  }, []);
  if (!data) return null;
  const maxCount = Math.max(1, ...data.days.map((d) => d.count));
  return (
    <div className="neo-card p-4 bg-[#CDB4DB]/30 dark:bg-[#1f1f3a]" data-testid="cleaning-insights">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <h4 className="font-bold font-['Fredoka'] flex items-center gap-1">
          <Sparkles className="w-4 h-4" /> Månadsinsikter · {data.month_label}
        </h4>
        <span className="text-xs font-bold px-3 py-1 rounded-full bg-white border-2 border-black">
          🔥 {data.current_streak} dagar i rad
        </span>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
        <div className="rounded-xl border-2 border-black p-2 bg-white text-center">
          <p className="text-[10px] opacity-70">Genomförda</p>
          <p className="font-bold font-['Fredoka'] text-lg">{data.total_completions}</p>
        </div>
        <div className="rounded-xl border-2 border-black p-2 bg-white text-center">
          <p className="text-[10px] opacity-70">Aktiva dagar</p>
          <p className="font-bold font-['Fredoka'] text-lg">{data.active_days}</p>
        </div>
        <div className="rounded-xl border-2 border-black p-2 bg-white text-center">
          <p className="text-[10px] opacity-70">Poäng denna månad</p>
          <p className="font-bold font-['Fredoka'] text-lg">+{data.total_points}</p>
        </div>
        <div className="rounded-xl border-2 border-black p-2 bg-white text-center">
          <p className="text-[10px] opacity-70">Mest v / m / år</p>
          <p className="font-bold font-['Fredoka'] text-xs">
            {data.freq_counts.weekly}v · {data.freq_counts.monthly}m · {data.freq_counts.yearly}å
          </p>
        </div>
      </div>
      {/* Bar chart */}
      <div className="bg-white rounded-xl border-2 border-black p-3 mb-3">
        <p className="text-[10px] font-bold uppercase opacity-60 mb-2">Genomförda per dag</p>
        <div className="flex items-end gap-0.5 h-20">
          {data.days.map((d) => {
            const h = d.count === 0 ? 4 : Math.max(6, (d.count / maxCount) * 80);
            return (
              <div
                key={d.date}
                className="flex-1 rounded-t-sm border border-black min-w-[3px]"
                style={{ height: `${h}px`, background: d.count === 0 ? '#eee' : '#B9FBC0' }}
                title={`${d.date}: ${d.count}`}
              />
            );
          })}
        </div>
      </div>
      {data.top_tasks?.length > 0 && (
        <div>
          <p className="text-[11px] font-bold opacity-80 mb-1">🏆 Mest städade denna månad</p>
          <ul className="text-xs space-y-0.5">
            {data.top_tasks.map((t) => (
              <li key={t.id}>• <span className="font-semibold">{t.title}</span> <span className="opacity-60">× {t.count}</span></li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function RoutineChain({ allTasks, onComplete, activeRoutine, setActiveRoutine, pointsToasts }) {
  const tasks = allTasks.filter((t) => t.routine === activeRoutine);
  const done = tasks.filter((t) => t.done_this_period).length;
  const total = tasks.length;
  const allDone = total > 0 && done === total;
  const next = tasks.find((t) => !t.done_this_period);

  const ROUTINES = [
    { k: 'morning', label: '☀️ Morgon-rutin', bg: 'from-[#FFE4B5] via-[#FFF8E7] to-[#FFEAA7]', accent: '#FFC8DD' },
    { k: 'evening', label: '🌙 Kväll-rutin', bg: 'from-[#E0CFFF] via-[#F3E8FF] to-[#C8B6FF]', accent: '#A2D2FF' },
  ];
  const meta = ROUTINES.find((r) => r.k === activeRoutine);

  return (
    <div className="neo-card p-4 overflow-hidden" style={{ background: 'linear-gradient(135deg, #FFF3F9 0%, #F3E8FF 50%, #E8F5FF 100%)' }} data-testid="cleaning-routine-chain">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
        <div className="flex gap-1">
          {ROUTINES.map((r) => (
            <button
              key={r.k}
              onClick={() => setActiveRoutine(r.k)}
              className={`neo-btn px-3 py-1.5 text-sm font-bold ${activeRoutine === r.k ? 'bg-white' : 'bg-white/50 opacity-70'}`}
              data-testid={`routine-tab-${r.k}`}
            >
              {r.label}
            </button>
          ))}
        </div>
        <span className="text-xs font-bold bg-white border-2 border-black rounded-full px-3 py-1">
          {done}/{total} klart
        </span>
      </div>

      {total === 0 ? (
        <div className="text-center py-4">
          <p className="text-sm opacity-70 mb-1">Inga uppgifter i denna rutin än</p>
          <p className="text-[11px] opacity-50 italic">Öppna en uppgift → välj "{meta?.label}" för att lägga till</p>
        </div>
      ) : allDone ? (
        <div className="text-center py-4">
          <div className="text-4xl mb-2">🎉</div>
          <p className="font-['Fredoka'] font-bold text-base">Hela rutinen klar!</p>
          <p className="text-xs opacity-70 italic">Ditt husdjur är stolt över dig 🐾</p>
        </div>
      ) : (
        <>
          {/* Next task — highlighted */}
          {next && (
            <div className="neo-card bg-white p-3 mb-2 flex items-center gap-3 animate-pulse-soft" data-testid="routine-next-task">
              <div className="text-2xl flex-shrink-0">➡️</div>
              <div className="flex-1 min-w-0">
                <p className="text-[10px] uppercase font-bold opacity-60 tracking-wider">Nästa steg</p>
                <p className="font-['Fredoka'] font-bold text-base truncate">{next.title}</p>
                <p className="text-[10px] opacity-70">+{next.points} poäng</p>
              </div>
              <button
                onClick={() => onComplete(next)}
                className="neo-btn px-4 py-2 bg-[#B9FBC0] font-bold text-sm flex-shrink-0"
                data-testid="routine-complete-next"
              >
                <Check className="w-4 h-4 inline" /> Klart!
              </button>
            </div>
          )}
          {/* Chain list */}
          <div className="space-y-1">
            {tasks.map((t, i) => (
              <div key={t.id} className={`flex items-center gap-2 text-xs p-1.5 rounded ${t.done_this_period ? 'opacity-50' : ''}`}>
                <div className={`w-5 h-5 rounded-full border-2 border-black flex items-center justify-center flex-shrink-0 ${t.done_this_period ? 'bg-[#B9FBC0]' : 'bg-white'}`}>
                  {t.done_this_period ? <Check className="w-3 h-3" /> : <span className="text-[10px] font-bold">{i + 1}</span>}
                </div>
                <span className={`${t.done_this_period ? 'line-through' : ''} truncate flex-1`}>{t.title}</span>
                <span className="text-[10px] opacity-60">+{t.points}p</span>
              </div>
            ))}
          </div>
        </>
      )}
    </div>
  );
}


function BulkQRPrintButton({ tasks }) {
  const [loading, setLoading] = useState(false);

  // HTML escape helper — prevents XSS when embedding user input in print HTML
  const escapeHtml = (s) => String(s ?? '').replace(/[&<>"']/g, (c) => ({
    '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;'
  }[c]));

  const printAll = async () => {
    if (!tasks.length) {
      toast.info('Inga uppgifter att skriva ut');
      return;
    }
    setLoading(true);
    try {
      // Fetch all scan tokens in parallel
      const results = await Promise.all(
        tasks.map(async (t) => {
          try {
            const { data } = await axios.get(`${API}/cleaning/tasks/${t.id}/scan-token`, { withCredentials: true });
            return { task: t, url: data.scan_url };
          } catch { return null; }
        })
      );
      const valid = results.filter(Boolean);
      if (!valid.length) {
        toast.error('Kunde inte generera QR-koder');
        return;
      }

      // Render QR codes in a hidden container
      const hidden = document.createElement('div');
      hidden.style.position = 'fixed';
      hidden.style.left = '-10000px';
      hidden.style.top = '0';
      document.body.appendChild(hidden);

      const root = (await import('react-dom/client')).createRoot(hidden);
      await new Promise((resolve) => {
        root.render(
          <div>
            {valid.map((v) => (
              <div key={v.task.id} id={`bulk-qr-${v.task.id}`}>
                <QRCodeSVG value={v.url} size={150} level="M" includeMargin={false} />
              </div>
            ))}
          </div>
        );
        setTimeout(resolve, 300);
      });

      // Build printable HTML with kawaii styling
      const cards = valid.map((v) => {
        const svg = document.getElementById(`bulk-qr-${v.task.id}`)?.innerHTML || '';
        const meta = FREQ_META[v.task.frequency] || FREQ_META.weekly;
        const bgMap = { daily: '#FFE4B5', weekly: '#C8B6FF', monthly: '#FFD6E8', yearly: '#B9F5D8' };
        const bg = bgMap[v.task.frequency] || '#FFF8E7';
        return `
          <div class="card" style="background:${bg}">
            <div class="title">${escapeHtml(v.task.title)}</div>
            <div class="qr">${svg}</div>
            <div class="meta">${escapeHtml(meta.label)} · +${Number(v.task.points) || 0} p</div>
            <div class="cue">Skanna när det är klart! 🐾</div>
          </div>
        `;
      }).join('');

      const w = window.open('', '_blank', 'width=900,height=1000');
      if (w) {
        w.document.write(`
          <!doctype html>
          <html><head>
          <title>QR-koder — Focus Critter</title>
          <style>
            @page { size: A4; margin: 12mm; }
            * { box-sizing: border-box; }
            body { font-family: 'Nunito', 'Helvetica Neue', sans-serif; margin: 0; padding: 8px; }
            h1 { font-size: 20px; text-align: center; margin: 0 0 4px; }
            p.sub { text-align: center; color: #666; font-size: 12px; margin: 0 0 14px; }
            .grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 10px; }
            .card {
              border: 3px solid #000; border-radius: 14px; box-shadow: 3px 3px 0 #000;
              padding: 10px; text-align: center; page-break-inside: avoid;
              display: flex; flex-direction: column; align-items: center; justify-content: center;
              min-height: 220px;
            }
            .title { font-weight: 800; font-size: 13px; line-height: 1.2; margin-bottom: 6px; min-height: 32px; }
            .qr { background: white; padding: 6px; border: 2px solid #000; border-radius: 8px; margin: 4px 0; line-height: 0; }
            .meta { font-size: 11px; font-weight: 700; margin-top: 6px; }
            .cue { font-size: 9px; color: #666; margin-top: 4px; }
            @media print {
              .grid { grid-template-columns: repeat(3, 1fr); gap: 8px; }
              body { padding: 0; }
            }
          </style>
          </head><body>
            <h1>🐾 Focus Critter — QR-koder</h1>
            <p class="sub">Klistra koderna där uppgifterna ska göras. Skanna = klart!</p>
            <div class="grid">${cards}</div>
            <script>window.onload = () => setTimeout(() => window.print(), 400);</script>
          </body></html>
        `);
        w.document.close();
      }

      // Cleanup hidden container
      setTimeout(() => {
        root.unmount();
        hidden.remove();
      }, 2000);
    } catch (e) {
      console.error(e);
      toast.error('Kunde inte förbereda utskrift');
    } finally {
      setLoading(false);
    }
  };

  return (
    <button
      onClick={printAll}
      disabled={loading || !tasks.length}
      className="neo-btn px-3 py-2 text-sm font-bold flex items-center gap-1 bg-[#E8D5FF] disabled:opacity-50"
      title="Skriv ut alla QR-koder på A4"
      data-testid="cleaning-print-all-qr"
    >
      <Printer className="w-4 h-4" /> {loading ? 'Genererar…' : `Skriv ut alla QR (${tasks.length})`}
    </button>
  );
}

export default function CleaningPage({ onPointsChange }) {
  const [today, setToday] = useState({ items: [], weekday: 0 });
  const [allTasks, setAllTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeFreq, setActiveFreq] = useState('daily');
  const [activeRoutine, setActiveRoutine] = useState(() => {
    const hour = new Date().getHours();
    return hour < 14 ? 'morning' : 'evening';
  });
  const [editing, setEditing] = useState(null); // task or 'new'
  const [qrTask, setQrTask] = useState(null);
  const [imgTask, setImgTask] = useState(null);

  const fetchToday = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/cleaning/today`, { withCredentials: true });
      setToday(data);
    } catch { /* noop */ }
  }, []);

  const fetchAll = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/cleaning/tasks`, { withCredentials: true });
      setAllTasks(data.items || []);
    } catch { /* noop */ }
  }, []);

  useEffect(() => {
    (async () => {
      setLoading(true);
      await Promise.all([fetchToday(), fetchAll()]);
      setLoading(false);
    })();
  }, [fetchToday, fetchAll]);

  // Daily morning reminder — fires once per day if user granted notifications.
  useEffect(() => {
    if (typeof window === 'undefined' || !('Notification' in window)) return;
    if (Notification.permission !== 'granted') return;
    if (!today.items?.length) return;
    const todayKey = new Date().toISOString().slice(0, 10);
    const lastKey = localStorage.getItem('cleaning_notif_day');
    if (lastKey === todayKey) return;
    const hour = new Date().getHours();
    if (hour < 7) return; // don't wake people up
    try {
      const titles = today.items.slice(0, 3).map((t) => t.title).join(', ');
      const rest = today.items.length > 3 ? ` +${today.items.length - 3} till` : '';
      new Notification('Focus Critter · Städpåminnelse 🧼', {
        body: `Idag ska du: ${titles}${rest}`,
        tag: 'cleaning-daily',
      });
      localStorage.setItem('cleaning_notif_day', todayKey);
    } catch { /* ignore */ }
  }, [today.items]);

  const reload = async () => { await Promise.all([fetchToday(), fetchAll()]); onPointsChange?.(); };

  const complete = async (task) => {
    try {
      const { data } = await axios.post(`${API}/cleaning/tasks/${task.id}/complete`, {}, { withCredentials: true });
      if (data.already_done) {
        toast.info('Redan avbockad denna period');
      } else {
        toast.success(`+${data.points_earned} poäng till ditt husdjur ✨`);
        if (data.pet_leveled_up) toast.success('Ditt husdjur går upp en nivå! ⭐');
        (data.unlocked_achievements || []).forEach((a) => {
          toast.success(`🏆 Ny medalj: ${a.emoji} ${a.title}! +${a.bonus_xp} bonus-XP`, { duration: 8000 });
        });
      }
      await reload();
    } catch { toast.error('Kunde inte bocka av'); }
  };

  const uncomplete = async (task) => {
    try {
      await axios.post(`${API}/cleaning/tasks/${task.id}/uncomplete`, {}, { withCredentials: true });
      await reload();
    } catch { toast.error('Kunde inte ångra'); }
  };

  const del = async (task) => {
    if (!window.confirm(`Ta bort "${task.title}"?`)) return;
    try {
      await axios.delete(`${API}/cleaning/tasks/${task.id}`, { withCredentials: true });
      await reload();
      toast.success('Borttagen');
    } catch { toast.error('Kunde inte ta bort'); }
  };

  const save = async (payload) => {
    try {
      if (editing && editing !== 'new') {
        await axios.patch(`${API}/cleaning/tasks/${editing.id}`, payload, { withCredentials: true });
        toast.success('Uppdaterad');
      } else {
        await axios.post(`${API}/cleaning/tasks`, payload, { withCredentials: true });
        toast.success('Tillagd ✨');
      }
      setEditing(null);
      await reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Kunde inte spara'); }
  };

  const requestNotifPermission = async () => {
    if (!('Notification' in window)) { toast.error('Notiser stöds inte i denna webbläsare'); return; }
    const p = await Notification.requestPermission();
    if (p === 'granted') {
      toast.success('Påminnelser aktiverade 🔔');
      // Fire a sample so user knows it works
      try {
        new Notification('Focus Critter · Städpåminnelse', {
          body: today.items.length > 0 ? `Idag ska du: ${today.items.slice(0,3).map(t=>t.title).join(', ')}` : 'Inget att städa idag — pusta ut!',
        });
      } catch { /* ignore */ }
    } else {
      toast.info('Du måste tillåta notiser i webbläsaren');
    }
  };

  if (loading) return <div className="neo-card p-8 text-center animate-pulse-soft">Laddar städlistan…</div>;

  const tasksForFreq = allTasks.filter((t) => t.frequency === activeFreq);
  const notifReady = typeof window !== 'undefined' && 'Notification' in window && Notification.permission === 'granted';

  return (
    <div className="space-y-4" data-testid="cleaning-page">
      {/* Help banner — discovery */}
      <CleaningHelpBanner />

      {/* Routine chain — Morgon/Kväll */}
      <RoutineChain
        allTasks={allTasks}
        onComplete={complete}
        activeRoutine={activeRoutine}
        setActiveRoutine={setActiveRoutine}
      />

      {/* Progress overview — tydlig överblick högst upp */}
      <ProgressOverview today={today} tasks={allTasks} />

      {/* Today banner */}
      <div className="neo-card p-5 bg-[#FFC8DD]" data-testid="cleaning-today-card">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <h2 className="text-xl font-bold font-['Fredoka'] flex items-center gap-2">
            <Sprout className="w-5 h-5" /> Idag
          </h2>
          <button
            onClick={requestNotifPermission}
            className={`neo-btn px-3 py-1.5 text-xs font-bold flex items-center gap-1 ${notifReady ? 'bg-[#B9FBC0]' : 'bg-white'}`}
            data-testid="cleaning-notif-btn"
          >
            <Bell className="w-3 h-3" /> {notifReady ? 'Notiser på' : 'Påminn mig'}
          </button>
        </div>
        {today.items.length === 0 ? (
          <p className="text-sm italic opacity-80">✨ Ingenting att städa idag — njut av din dag!</p>
        ) : (
          <>
            <p className="text-sm mb-2">
              Idag ska du: <strong>{today.items.slice(0, 3).map((t) => t.title).join(', ')}</strong>
              {today.items.length > 3 && <span> + {today.items.length - 3} till</span>}
            </p>
            <div className="space-y-2 max-h-80 overflow-y-auto pr-1" data-testid="cleaning-today-list">
              {today.items.map((t) => (
                <TaskRow key={t.id} task={t} onComplete={complete} onUncomplete={uncomplete} onDelete={del} onEdit={setEditing} onShowQR={setQrTask} onShowImage={setImgTask} />
              ))}
            </div>
          </>
        )}
      </div>

      {/* Monthly insights */}
      <InsightsCard />

      {/* Achievements */}
      <AchievementsCard />

      {/* Frequency sub-tabs */}
      <div className="flex flex-wrap gap-2" data-testid="cleaning-freq-tabs">
        {Object.entries(FREQ_META).map(([k, m]) => {
          const Icon = m.icon;
          const active = activeFreq === k;
          return (
            <button
              key={k}
              onClick={() => setActiveFreq(k)}
              className={`neo-btn px-3 py-2 text-sm font-bold flex items-center gap-1 ${active ? m.color : 'bg-white'}`}
              data-testid={`cleaning-freq-tab-${k}`}
            >
              <Icon className="w-4 h-4" /> {m.label}
            </button>
          );
        })}
        <button
          onClick={() => setEditing('new')}
          className="neo-btn px-3 py-2 text-sm font-bold flex items-center gap-1 bg-[#FDF0D5] ml-auto"
          data-testid="cleaning-add-btn"
        >
          <Plus className="w-4 h-4" /> Ny uppgift
        </button>
        <BulkQRPrintButton tasks={allTasks} />
      </div>

      {editing && (
        <TaskEditor
          existing={editing === 'new' ? null : editing}
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      )}

      {/* Summary for current freq */}
      <SummaryCard freq={activeFreq} />

      {/* Tasks for current freq */}
      <div className="space-y-2" data-testid={`cleaning-list-${activeFreq}`}>
        {tasksForFreq.length === 0 ? (
          <div className="neo-card p-6 text-center bg-white dark:bg-[#252545]">
            <p className="opacity-70 text-sm">Inga uppgifter i denna frekvens. Lägg till en med "Ny uppgift" ✨</p>
          </div>
        ) : (
          tasksForFreq.map((t) => (
            <TaskRow key={t.id} task={t} onComplete={complete} onUncomplete={uncomplete} onDelete={del} onEdit={setEditing} onShowQR={setQrTask} onShowImage={setImgTask} />
          ))
        )}
      </div>

      {qrTask && <QRModal task={qrTask} onClose={() => setQrTask(null)} />}
      {imgTask && <ReferenceImageModal task={imgTask} onClose={() => setImgTask(null)} />}
    </div>
  );
}
